'use client';

import { useState } from 'react';
import { type Control, useForm } from 'react-hook-form';
import { ActionButton, FormFile, FormSelect, FormText, FormTextArea } from '../../../../components/form';
import layoutStyles from '../../../../styles/modules/layout.module.scss';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import type { ContactFormProps, ContactFormValues } from '../../types';

export interface ContactFormUIProps extends ContactFormProps {
  onSubmit: (values: {
    selectCategory: { id: number; name: string };
    name: string;
    email: string;
    description: string;
    files: File[];
  }) => void;
  onError: () => void;
}

function ContactForm({ categories, name, email, onSubmit, onError }: ContactFormUIProps) {
  const [isLoading, setIsLoading] = useState(false);
  const isLoggedIn = email !== '';
  const attachFileNum = isLoggedIn ? 5 : 1;

  const { handleSubmit, getValues, control } = useForm<ContactFormValues>({
    mode: 'onChange',
    defaultValues: {
      selectCategoryId: 0,
      name: name || '',
      email: email || '',
      description: '',
      file0: null,
      file1: null,
      file2: null,
      file3: null,
      file4: null,
    },
  });

  const getFileFormVal = (formNum: number) => {
    return [...Array(formNum)]
      .map((_, i) => getValues(`file${i}` as keyof ContactFormValues))
      .filter((f): f is File => f != null);
  };

  const getSelectCategory = (selectCategoryId: number) => {
    return categories.find((category) => category.id === selectCategoryId) as { id: number; name: string };
  };

  const submit = async () => {
    try {
      setIsLoading(true);
      onSubmit({
        selectCategory: getSelectCategory(Number(getValues('selectCategoryId'))),
        name: getValues('name'),
        email: getValues('email'),
        description: getValues('description'),
        files: getFileFormVal(attachFileNum),
      });
    } catch {
      setIsLoading(false);
    }
  };

  const fileForm = (formNum: number, control: Control<ContactFormValues>) => {
    return [...Array(formNum)].map((_, i) => (
      <FormFile
        // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
        key={i}
        label={i === 0 ? '添付画像（JPG/PNG/WEBP/HEIC/MP4/MOV/QT/HEVC 1GBまで）' : ''}
        name={`file${i}` as keyof ContactFormValues}
        control={control}
      />
    ));
  };

  const emptyOption = [{ value: 0, text: '--選択してください--' }];
  const categoryOption = categories.map((item) => ({
    value: item.id,
    text: item.name,
  }));
  const totalOption = emptyOption.concat(categoryOption);

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <div className={layoutStyles.formWrapper}>
        <FormSelect
          label='問い合わせ内容'
          name='selectCategoryId'
          items={totalOption}
          control={control}
          required
          outlined
        />
        <FormText label='名前' name='name' control={control} readOnly={isLoggedIn} required />
        <FormText label='メールアドレス' name='email' control={control} readOnly={isLoggedIn} required />
        <FormTextArea label='問い合わせ内容' name='description' control={control} required outlined={true} />
        {fileForm(attachFileNum, control)}
      </div>
      <div className={`${layoutStyles.centeredBtnWrapper} ${utilsStyles.mtMedium}`}>
        <ActionButton onClick={handleSubmit(submit, onError)} disabled={isLoading}>
          確認する
        </ActionButton>
      </div>
    </form>
  );
}

export default ContactForm;
